import { buildConfigApiUrl } from '@api/api';

/**
 * Fetch recipients (contacts and/or contact groups) for scheduled reports
 *
 * @param {Object} options - Options for fetching recipients
 * @param {string} options.type - Filter by type: 'contacts' or 'contactGroups'. Omit or pass 'both' for all.
 * @param {string} options.search - Optional search term to filter by name or email
 * @returns {Promise<Object>} Response with status and data array of recipients
 */
export const fetchRecipients = async ({ type, search = '' } = {}) => {
  try {
    const params = new URLSearchParams();
    if (type && type !== 'both') {
      params.append('type', type);
    }
    if (search) {
      params.append('search', search);
    }

    const queryString = params.toString();
    const url = buildConfigApiUrl(`/contact-groups/recipients${queryString ? `?${queryString}` : ''}`);

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to fetch recipients');
    }

    const result = await response.json();
    return {
      status: 'success',
      data: result.data || [],
    };
  } catch (error) {
    return {
      status: 'error',
      message: error.message,
      data: [],
    };
  }
};

/**
 * Fetch only individual contacts
 *
 * @param {string} search - Optional search term
 * @returns {Promise<Object>} Response with status and data array of contacts
 */
export const fetchContacts = async (search = '') => fetchRecipients({ type: 'contacts', search });

/**
 * Fetch only contact groups
 *
 * @param {string} search - Optional search term
 * @returns {Promise<Object>} Response with status and data array of contact groups
 */
export const fetchContactGroupsAsRecipients = async (search = '') => fetchRecipients({ type: 'contactGroups', search });

